import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";

type FaqSectionProps = {
  onGetStarted?: () => void;
};

export default function FaqSection({ onGetStarted }: FaqSectionProps) {
  const { t } = useTranslation();

  // Animated CTA button styles
  const animatedCTAStyle = `
    @keyframes faqCtaPulse {
      0%, 100% { 
        opacity: 1;
        transform: scale(1);
      }
      50% { 
        opacity: 0.95;
        transform: scale(1.02);
      }
    }
    @keyframes faqCtaShimmer {
      0% { transform: translateX(-100%) skewX(-15deg); }
      100% { transform: translateX(200%) skewX(-15deg); }
    }
    @keyframes faqCtaGradient {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    @keyframes faqIconPulse {
      0%, 100% { transform: scale(1) rotate(0deg); }
      50% { transform: scale(1.2) rotate(10deg); }
    }
    .faq-animated-cta {
      animation: faqCtaPulse 2s ease-in-out infinite;
      background-image: linear-gradient(90deg, #f97316, #ea580c, #ec4899, #9333ea, #ec4899, #ea580c, #f97316);
      position: relative;
      overflow: hidden;
    }
    .faq-animated-cta::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 200%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
      animation: faqCtaGradient 3s ease infinite;
      z-index: 1;
    }
    .faq-animated-cta .faq-icon {
      animation: faqIconPulse 1.5s ease-in-out infinite;
    }
    .faq-animated-cta:hover {
      animation: faqCtaPulse 1s ease-in-out infinite !important;
      transform: scale(1.05);
    }
    .faq-animated-cta:hover::before {
      animation: faqCtaGradient 2s ease infinite !important;
    }
    .faq-animated-cta:hover .faq-icon {
      animation: faqIconPulse 0.8s ease-in-out infinite;
    }
  `;
  
  // Helper function to ensure translation returns a string
  // Prevents React error #31: "Objects are not valid as a React child"
  const safeTranslate = (key: string, fallback: string = ''): string => {
    try {
      const translated = t(key);
      
      // If translation returns an object (shouldn't happen with returnObjects: false, but safety check)
      if (typeof translated === 'object' && translated !== null) {
        // If it's an object with question/answer keys, extract the appropriate value
        if ('question' in translated && 'answer' in translated) {
          console.warn(`Translation key "${key}" returned an object with {question, answer}. Using fallback.`);
          return fallback;
        }
        // For any other object, return fallback
        console.warn(`Translation key "${key}" returned an object instead of a string`);
        return fallback;
      }
      
      // Ensure it's a string (handle null, undefined, numbers, etc.)
      if (translated === null || translated === undefined) {
        return fallback;
      }
      
      const result = String(translated);
      
      // If result is empty or just the key (translation missing), use fallback
      if (!result || result === key) {
        return fallback;
      }
      
      return result;
    } catch (error) {
      console.warn(`Translation error for key "${key}":`, error);
      return fallback;
    }
  };
  
  const faqs = [
    {
      id: 1,
      question: safeTranslate('faq.questions.atsRejected.question', 'Why is my resume rejected by ATS systems?'),
      answer: safeTranslate('faq.questions.atsRejected.answer', 'Most resumes are rejected because they lack ATS-friendly formatting, relevant keywords, or alignment with job descriptions.'),
      iconBg: "bg-orange-500",
    },
    {
      id: 2,
      question: safeTranslate('faq.questions.howHiringWorks.question', 'How do modern hiring systems actually work?'),
      answer: safeTranslate('faq.questions.howHiringWorks.answer', 'Most companies use ATS systems that automatically screen resumes before humans see them.'),
      iconBg: "bg-emerald-500",
    },
    {
      id: 3,
      question: safeTranslate('faq.questions.linkedinVisibility.question', 'Why do recruiters not view my LinkedIn profile?'),
      answer: safeTranslate('faq.questions.linkedinVisibility.answer', 'Recruiters search LinkedIn using keywords, titles, and activity signals.'),
      iconBg: "bg-blue-500",
    },
    {
      id: 4,
      question: safeTranslate('faq.questions.skillsGap.question', 'How do I identify skill gaps in my career?'),
      answer: safeTranslate('faq.questions.skillsGap.answer', 'MotivaMatch compares your current skills with market demand and target roles.'),
      iconBg: "bg-pink-500",
    },
    {
      id: 5,
      question: safeTranslate('faq.questions.companyTargeting.question', 'How does company matching work?'),
      answer: safeTranslate('faq.questions.companyTargeting.answer', 'Our AI analyzes job descriptions and company data to match your skills with relevant opportunities.'),
      iconBg: "bg-slate-600",
    },
    {
      id: 6,
      question: safeTranslate('faq.questions.interviewPreparation.question', 'How does interview preparation work?'),
      answer: safeTranslate('faq.questions.interviewPreparation.answer', 'Our AI generates job-specific interview questions and provides role-specific coaching.'),
      iconBg: "bg-indigo-500",
    },
    {
      id: 7,
      question: safeTranslate('faq.questions.languagesGlobal.question', 'Is MotivaMatch valid internationally?'),
      answer: safeTranslate('faq.questions.languagesGlobal.answer', 'Yes. MotivaMatch is available in English, French, and Spanish, and supports global job markets.'),
      iconBg: "bg-violet-500",
    },
    {
      id: 8,
      question: safeTranslate('faq.questions.dataSafety.question', 'Is my data safe and secure?'),
      answer: safeTranslate('faq.questions.dataSafety.answer', 'Yes. We use bank-level encryption and follow strict security protocols.'),
      iconBg: "bg-teal-500",
    },
    {
      id: 9,
      question: safeTranslate('faq.questions.careerStage.question', 'Is MotivaMatch suitable for my career stage?'),
      answer: safeTranslate('faq.questions.careerStage.answer', 'Yes. MotivaMatch is designed for professionals at every career stage.'),
      iconBg: "bg-cyan-500",
    },
  ];
  const [openFaqId, setOpenFaqId] = useState<number | null>(null);

  const handleGetStarted = () => {
    if (onGetStarted) {
      onGetStarted();
    } else {
      // Fallback: scroll to hero section
      const heroSection = document.getElementById('hero');
      if (heroSection) {
        heroSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const toggleFaq = (id: number) => {
    setOpenFaqId(openFaqId === id ? null : id);
  };

  return (
    <>
      <style>{animatedCTAStyle}</style>
      <section className="bg-white py-16" id="faq">
      <div className="mx-auto max-w-5xl px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 text-white shadow-lg shadow-purple-200/50">
            <span className="text-2xl font-bold">?</span>
          </div>
          <h2 className="bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 bg-clip-text text-3xl font-extrabold text-transparent md:text-4xl mb-3">
            {t('faq.title')}
          </h2>
          <p className="text-sm text-gray-600 md:text-base max-w-2xl mx-auto">
            {t('faq.subtitle')}
          </p>
        </div>

        {/* FAQ grid */}
        <div className="grid gap-4 md:grid-cols-2">
          {faqs.map((item, index) => {
            const isOpen = openFaqId === item.id;
            return (
              <div
                key={item.id}
                className={`group relative flex flex-col rounded-xl transition-all duration-300 overflow-hidden bg-white shadow-md hover:shadow-lg ${
                  isOpen 
                    ? 'shadow-lg' 
                    : ''
                }`}
              >
                <button
                  onClick={() => toggleFaq(item.id)}
                  className="w-full p-5 text-left focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 rounded-xl transition-colors duration-200"
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${item.id}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Number Badge - Square */}
                    <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center text-white shadow-sm transition-all duration-300 font-bold text-lg ${
                      isOpen ? 'scale-105 shadow-md' : 'group-hover:scale-105'
                    } ${item.iconBg}`}>
                      {index + 1}
                    </div>
                    
                    {/* Question - Consistent typography */}
                    <h3 className="flex-1 text-base font-bold text-gray-900 md:text-lg leading-tight pr-3">
                      {item.question}
                    </h3>
                    
                    {/* Chevron - Consistent styling */}
                    <div className={`flex-shrink-0 flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                      isOpen 
                        ? 'text-gray-600 rotate-180' 
                        : 'text-gray-400 group-hover:text-gray-600'
                    }`}>
                      <ChevronDown className="w-5 h-5" />
                    </div>
                  </div>
                </button>
                
                {/* Answer - Smooth expand/collapse */}
                <div
                  id={`faq-answer-${item.id}`}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-5 pb-5 pt-0">
                    <div className="ml-20 pl-4">
                      <p className="text-sm leading-relaxed text-gray-700 md:text-[15px]">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <div className="bg-gradient-to-r from-orange-50 via-pink-50 to-purple-50 rounded-2xl p-8 border border-orange-100">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
              {t('faq.cta.title')}
            </h3>
            <p className="text-sm text-gray-600 md:text-base mb-6 max-w-xl mx-auto">
              {t('faq.cta.description.prefix')}{" "}
              <a
                href="mailto:support@motivamatch.com"
                className="text-orange-600 hover:text-purple-600 font-semibold underline decoration-2 underline-offset-2 transition-colors"
              >
                support@motivamatch.com
              </a>
              {t('faq.cta.description.suffix')}
            </p>

            <div className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
              <button
                onClick={handleGetStarted}
                className="faq-animated-cta group relative inline-flex w-full sm:w-auto items-center justify-center rounded-full px-8 py-3.5 text-sm font-bold text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 overflow-hidden"
              >
                {/* Glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 rounded-full blur opacity-50 group-hover:opacity-75 transition-opacity duration-300 -z-10"></div>
                <span className="faq-icon relative z-10 mr-2 text-lg">⚡</span>
                <span className="relative z-10">{t('faq.cta.button')}</span>
              </button>
              <a
                href="mailto:support@motivamatch.com"
                className="inline-flex w-full sm:w-auto items-center justify-center rounded-full border-2 border-gray-300 bg-white px-8 py-3.5 text-sm font-semibold text-gray-800 shadow-sm transition-all duration-300 hover:bg-gray-50 hover:border-gray-400"
              >
                {t('faq.cta.contactSupport')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
}

